// Home: the modules in the order of the workflow (DFT -> Tight Binding -> Properties or NEGF Transport; then Spin-Valley
// Qubits and QTetris), each card opening the module's home page; the projects to continue; where to start.
import { Anchor, Box, Card, Group, SimpleGrid, Stack, Table, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { JobStatusBadge, useKindLabel } from "../components/Jobs";
import { PageTitle } from "../components/Layout";
import { ModuleBadge, ModuleCard } from "../components/Modules";
import { formatDate, get, type Job, type Project } from "../lib/api";
import { useExamples, type Example } from "../lib/examples";
import { useMe } from "../lib/hooks";
import { examplesPath, moduleBySlug, moduleProducts, type ModuleInfo, type ModuleSlug } from "../lib/modules";

/** Where a newcomer can start: one illustrative project per module (shown if it exists on this server). */
const STARTERS: { slug: ModuleSlug; id: string; what: string }[] = [
  { slug: "tight-binding", id: "graphene-basic", what: "your first fit" },
  { slug: "properties", id: "props-haldane", what: "a Chern insulator" },
  { slug: "negf", id: "negf-zigzag-ribbon", what: "conductance steps" },
  { slug: "dft", id: "dft-si", what: "a first band structure" },
];

const Arrow = () => (
  <Box style={{ display: "flex", alignItems: "center", justifyContent: "center", color: "var(--mantine-color-gray-5)", fontSize: 22 }} aria-hidden>➜</Box>
);

export function Home() {
  const { data: me } = useMe();
  const projects = useQuery({ queryKey: ["projects"], queryFn: () => get<Project[]>("/projects") });
  const jobs = useQuery({ queryKey: ["jobs", "home"], queryFn: () => get<Job[]>("/jobs?limit=50") });
  const examples = useExamples();
  const label = useKindLabel();
  const wide = useMediaQuery("(min-width: 62em)");
  if (!me) return null;

  const all = [...(projects.data ?? [])].sort((a, b) => b.updated_at.localeCompare(a.updated_at));
  const lastJob = (p: Project) => (jobs.data ?? []).find((j) => j.project_id === p.id);
  const ofModule = (m: ModuleInfo) => all.filter((p) => moduleProducts(m).includes(p.product));
  const exCount = (m: ModuleInfo) => (examples.data ?? []).filter((e: Example) => (e.product ?? "skfitter") === m.examplesProduct).length;
  const card = (slug: ModuleSlug, compact = false) => {
    const m = moduleBySlug(slug)!;
    const mine = ofModule(m);
    const latest = mine[0] && { name: mine[0].name, id: mine[0].id, what: lastJob(mine[0]) ? label(lastJob(mine[0])!.kind) : "" };
    return <ModuleCard m={m} projects={mine.length} examples={exCount(m)} latest={latest} compact={compact} />;
  };
  const starters = STARTERS.map((s) => ({ ...s, ex: examples.data?.find((e) => e.id === s.id) })).filter((s) => s.ex);
  const first = me.name && !me.name.includes("@") ? `, ${me.name.split(" ")[0]}` : "";

  return (
    <Stack gap="md">
      <PageTitle title={`Welcome${first}`}>
        <Text size="sm" c="dimmed">
          {me.usage.projects} of {String(me.limits["storage.max_projects"])} projects · {me.usage.cpu_hours_month.toFixed(1)} CPU-h
          this month · <Anchor component={Link} to="/account" size="sm">My plan &amp; usage</Anchor>
        </Text>
      </PageTitle>
      <Text c="dimmed" mt={-12}>Choose a module to see its projects, illustrative projects and what it can do.</Text>

      <Text fw={600} size="lg" mt="xs">From first principles to devices</Text>
      <Box data-testid="pipeline" style={{ display: "grid", gap: 12, alignItems: "center",
        gridTemplateColumns: wide ? "1fr 30px 1fr 30px 1fr" : "1fr" }}>
        {card("dft")}{wide && <Arrow />}{card("tight-binding")}{wide && <Arrow />}
        <Stack gap="sm">{card("properties", true)}{card("negf", true)}</Stack>
      </Box>
      <Text size="xs" c="dimmed" mt={-6}>
        DFT bands → a tight-binding model fitted to them → its properties, or a device built from it. Each module also works
        on its own (your files, Wannier90, textbook models).
      </Text>

      <Text fw={600} size="lg" mt="xs">Devices, qubits and the classroom</Text>
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm">
        {card("spin-valley-qubits", true)}{card("qtetris", true)}
      </SimpleGrid>

      <Box mt="xs" style={{ display: "grid", gap: 16, alignItems: "start", gridTemplateColumns: wide ? "1.7fr 1fr" : "1fr" }}>
        <Card withBorder>
          <Group justify="space-between" mb="xs">
            <Text fw={600}>Continue where you left off</Text>
            <Anchor component={Link} to="/projects" size="sm">All projects</Anchor>
          </Group>
          {!all.length ? <Text c="dimmed" size="sm">No projects yet. Open a module to start one, or begin with an illustrative project.</Text> : (
            <Table.ScrollContainer minWidth={620}>
              <Table verticalSpacing={6} data-testid="recent-projects">
                <Table.Thead><Table.Tr><Table.Th>Project</Table.Th><Table.Th>Module</Table.Th><Table.Th>Last calculation</Table.Th><Table.Th>Updated</Table.Th></Table.Tr></Table.Thead>
                <Table.Tbody>
                  {all.slice(0, 6).map((p) => {
                    const j = lastJob(p);
                    return (
                      <Table.Tr key={p.id}>
                        <Table.Td><Anchor component={Link} to={`/projects/${p.id}`} size="sm">{p.name}</Anchor></Table.Td>
                        <Table.Td><ModuleBadge product={p.product} /></Table.Td>
                        <Table.Td>{j ? <Group gap={6} wrap="nowrap"><Text size="sm">{label(j.kind)}</Text><JobStatusBadge status={j.status} /></Group> : <Text size="sm" c="dimmed">-</Text>}</Table.Td>
                        <Table.Td><Text size="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>{formatDate(p.updated_at)}</Text></Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Card>
        <Card withBorder>
          <Text fw={600} mb="xs">New to PRAYOG-QP?</Text>
          <Text size="sm">
            Start from an <b>illustrative project</b>: a finished set-up with a guide, opened as your own copy and run step by step.
          </Text>
          <Stack gap={6} mt="sm">
            {starters.map((s, i) => (
              <Text size="sm" key={s.id}>
                {i + 1}. <Anchor component={Link} to={examplesPath(s.slug, s.id)} size="sm">{s.ex!.name}</Anchor>
                <Text span c="dimmed" size="sm"> — {moduleBySlug(s.slug)!.name}: {s.what}</Text>
              </Text>
            ))}
          </Stack>
          <Text size="xs" c="dimmed" mt="sm">Every module's page lists its own illustrative projects.</Text>
        </Card>
      </Box>
    </Stack>
  );
}
